import type { FC, ReactNode } from 'react'
import type { PracticeQuestion } from '@/sanskrit/lib/practice/generators'

import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { Seo } from '@/sanskrit/components/generals/seo'
import { Layout } from '@/sanskrit/components/generals/layout'
import { useAppState, useT } from '@/sanskrit/components/providers/app-state'
import { buildReview } from '@/sanskrit/lib/practice/review'
import PracticeSession from '@/sanskrit/components/practice/practice-session'

const ReviewPage: FC = (): ReactNode => {
    const t = useT()
    const router = useRouter()
    const { progress, hydrated } = useAppState()
    const [questions, setQuestions] = useState<PracticeQuestion[] | null>(null)
    const [info, setInfo] = useState<{ due: number; drills: number; mixed: number } | null>(null)
    const [round, setRound] = useState(0)
    const mode = router.query.mode === 'revision' ? 'revision' : 'due'

    useEffect(() => {
        if (!hydrated || !router.isReady) return
        const plan = buildReview(progress, { size: 10, seed: Date.now() % 1e9, mode })
        setQuestions(plan.questions)
        setInfo(plan.reasons)
        // Build once per round; answers during the round must not reshuffle it.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hydrated, router.isReady, round, mode])

    return (
        <Layout>
            <Seo title='Daily review' description='A daily Sanskrit review round built by spaced repetition from the words and forms that are due today and from your most frequent mistakes.' />
            <div className='mx-auto max-w-3xl px-4 pt-6 pb-16 sm:px-6'>
                {info && questions && (
                    <p className='mb-2 text-sm text-muted-foreground'>
                        {t({
                            hi: `इस दौर में: ${info.due} दोहराने योग्य, ${info.drills} कमज़ोरी-अभ्यास, ${info.mixed} मिश्रित।`,
                            en: `This round: ${info.due} due for review, ${info.drills} targeted drills, ${info.mixed} mixed.`
                        })}
                    </p>
                )}
                {questions ? (
                    <PracticeSession
                        key={round}
                        questions={questions}
                        title={mode === 'revision' ? { hi: 'मेरी पुनरावृत्ति-सूची', en: 'My revision list' } : { hi: 'आज की पुनरावृत्ति', en: 'Today’s review' }}
                        sa='पुनरावृत्तिः'
                        gameId='review'
                        onRestart={() => setRound(r => r + 1)}
                    />
                ) : (
                    <p className='py-24 text-center text-muted-foreground'>{t({ hi: 'आपकी पुनरावृत्ति तैयार हो रही है...', en: 'Preparing your review…' })}</p>
                )}
            </div>
        </Layout>
    )
}

export default ReviewPage
